import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2, Video, X } from 'lucide-react';
import {
  BrandBriefFeedItem,
  BrandProposalKey,
  getProposalUploadKey,
  sendBrandProposal,
  uploadDraftFile,
} from '../../api/payment/brand-deal.api';
import { contentFormatHint } from '../../utils/brand-content-format';
import { PROPOSAL_COMPOSER_NOTE, PROPOSAL_LIMITS, validateProposal } from '../../utils/brand-proposal';

/**
 * Hộp gửi đề xuất cho một chiến dịch "Nhận đề xuất" (spec 2026-10-12 §6). Mở là xin khoá tải lên (kiểm điều kiện như ứng tuyển), file tải qua
 * đường tải nháp sẵn có bằng khoá đó, xem trước bằng URL cục bộ (không xin vé), kiểm định dạng trước khi gửi. Giá cố định theo suất.
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

// jsdom không có URL.createObjectURL — thiếu thì không có ảnh xem trước, file vẫn tải và gửi bình thường.
const localPreview = (file: File): string | null =>
  typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;

export interface ProposalAttachment {
  fileId: string;
  kind: 'image' | 'video';
  previewUrl: string | null;
}
type Attachment = ProposalAttachment;

/** Thu hồi URL xem trước cục bộ (jsdom không có hàm này). */
export const revokePreview = (a: Attachment) => {
  if (a.previewUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(a.previewUrl);
};

export const REMOVE_ATTACHMENT_CONFIRM = 'Bỏ file này? File đã tải vẫn tính vào số lượt tải trong 24 giờ.';

// Ô nào được chọn theo định dạng chiến dịch (không rõ định dạng thì cho cả hai, máy chủ vẫn kiểm lại).
const allowsImages = (format?: string | null) => format !== 'Video';
const allowsVideo = (format?: string | null) => format !== 'Image' && format !== 'Article';

// Khai ngoài component cha (landmine remount) — một file đã tải kèm nút bỏ.
const AttachmentThumb: React.FC<{ a: Attachment; onRemove: () => void }> = ({ a, onRemove }) => (
  <div className="relative size-20 shrink-0 overflow-hidden rounded-lg border ui-divider bg-slate-100 dark:bg-slate-800">
    {a.previewUrl && a.kind === 'image' && <img src={a.previewUrl} alt="" className="size-full object-cover" />}
    {a.previewUrl && a.kind === 'video' && <video src={a.previewUrl} className="size-full object-cover" muted />}
    {!a.previewUrl && (
      <span className="flex size-full items-center justify-center text-[11px] ui-muted">{a.kind === 'video' ? 'Video' : 'Ảnh'}</span>
    )}
    <button type="button" onClick={onRemove} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80">
      <X size={12} />
    </button>
  </div>
);

export interface BrandProposalComposerProps {
  item: BrandBriefFeedItem;
  onClose: () => void;
  onSent: () => void;
  /** File đã tải của lần mở trước (cha giữ để đóng rồi mở lại không mất). */
  initialAttachments?: Attachment[];
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  /** Lỗi cần xử lý riêng (điều khoản thuế) — trả true nếu đã xử lý, khi đó không hiện toast lỗi. */
  handleError?: (err: unknown, retry: () => void) => boolean;
}

const BrandProposalComposer: React.FC<BrandProposalComposerProps> = ({ item, onClose, onSent, handleError, initialAttachments, onAttachmentsChange }) => {
  const [key, setKey] = useState<BrandProposalKey | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>(initialAttachments ?? []);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    getProposalUploadKey(item.campaignId)
      .then((k) => {
        if (alive) setKey(k);
      })
      .catch((err) => {
        if (alive) setKeyError(errorMessage(err, 'Không mở được khung đề xuất lúc này.'));
      });
    return () => {
      alive = false;
    };
  }, [item.campaignId]);

  const changeRef = useRef(onAttachmentsChange);
  changeRef.current = onAttachmentsChange;
  useEffect(() => {
    changeRef.current?.(attachments);
  }, [attachments]);

  const removeAttachment = (a: Attachment) => {
    if (!window.confirm(REMOVE_ATTACHMENT_CONFIRM)) return;
    revokePreview(a);
    setAttachments((prev) => prev.filter((x) => x.fileId !== a.fileId));
  };

  const images = attachments.filter((a) => a.kind === 'image').length;
  const videos = attachments.filter((a) => a.kind === 'video').length;

  const upload = async (files: File[]) => {
    if (!key) return;
    setUploading(true);
    try {
      for (const file of files) {
        setProgress(0);
        // eslint-disable-next-line no-await-in-loop -- tải lần lượt để thanh tiến độ đúng từng file
        const res = await uploadDraftFile(key.uploadKey, file, setProgress);
        setAttachments((prev) => [...prev, { fileId: res.fileId, kind: res.kind, previewUrl: localPreview(file) }]);
      }
    } catch (err) {
      toast.error(errorMessage(err, 'Tải file thất bại.'));
    } finally {
      setUploading(false);
    }
  };

  const onPickImages = (input: HTMLInputElement) => {
    const picked = Array.from(input.files ?? []);
    input.value = ''; // chọn lại đúng file vừa chọn vẫn nhận
    if (picked.length === 0) return;
    if (picked.length > PROPOSAL_LIMITS.maxImages - images) {
      toast.error(`Tối đa ${PROPOSAL_LIMITS.maxImages} ảnh.`);
      return;
    }
    upload(picked);
  };

  const onPickVideo = (input: HTMLInputElement) => {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (videos >= PROPOSAL_LIMITS.maxVideos) {
      toast.error(`Tối đa ${PROPOSAL_LIMITS.maxVideos} video.`);
      return;
    }
    upload([file]);
  };

  const submit = async () => {
    const error = validateProposal(item.contentFormat ?? null, text, images, videos);
    if (error) {
      toast.error(error);
      return;
    }
    setSending(true);
    try {
      await sendBrandProposal(item.campaignId, { text: text.trim(), fileIds: attachments.map((a) => a.fileId) });
      toast.success('Đã gửi đề xuất cho người đặt.');
      onSent();
    } catch (err) {
      if (handleError && handleError(err, submit)) return;
      toast.error(errorMessage(err, 'Không gửi được đề xuất.'));
    } finally {
      setSending(false);
    }
  };

  const hint = contentFormatHint(item.contentFormat);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="ui-card flex max-h-[90vh] w-full max-w-[560px] flex-col gap-3 overflow-y-auto p-4">
        <h2 className="ui-heading text-[17px] font-bold">Gửi đề xuất</h2>
        <div className="ui-muted text-[13px]">
          {item.brandName} · {item.title}
        </div>
        {hint && <p className="ui-muted text-[12px]">{hint}</p>}

        {keyError ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
            {keyError}
          </p>
        ) : (
          <>
            <label className="block text-sm ui-text">
              Nội dung đề xuất
              <textarea
                className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none"
                rows={6}
                value={text}
                maxLength={PROPOSAL_LIMITS.textMaxLength}
                onChange={(e) => setText(e.target.value)}
              />
            </label>
            <span className="-mt-2 block text-[11px] ui-muted">{text.length}/{PROPOSAL_LIMITS.textMaxLength}</span>

            <div className="flex flex-wrap gap-2">
              {attachments.map((a) => (
                <AttachmentThumb key={a.fileId} a={a} onRemove={() => removeAttachment(a)} />
              ))}
            </div>

            {key && (
              <div className="flex flex-wrap items-center gap-2">
                {allowsImages(item.contentFormat) && (
                  <label className="flex cursor-pointer items-center gap-1 rounded-md border ui-divider px-3 py-1.5 text-[13px] font-semibold ui-text">
                    <ImagePlus size={14} /> Thêm ảnh
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={uploading} onChange={(e) => onPickImages(e.target)} />
                  </label>
                )}
                {allowsVideo(item.contentFormat) && videos < PROPOSAL_LIMITS.maxVideos && (
                  <label className="flex cursor-pointer items-center gap-1 rounded-md border ui-divider px-3 py-1.5 text-[13px] font-semibold ui-text">
                    <Video size={14} /> Thêm video
                    <input type="file" accept="video/mp4,video/quicktime,video/webm" hidden disabled={uploading} onChange={(e) => onPickVideo(e.target)} />
                  </label>
                )}
                {uploading && (
                  <span className="flex items-center gap-1 text-[12px] ui-muted">
                    <Loader2 size={14} className="animate-spin" /> {progress}%
                  </span>
                )}
              </div>
            )}

            <p className="ui-muted text-[12px]">{PROPOSAL_COMPOSER_NOTE}</p>
          </>
        )}

        <div className="flex gap-2">
          {!keyError && (
            <button
              type="button"
              disabled={sending || uploading}
              onClick={submit}
              className="rounded-lg bg-[#0057FF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {sending ? 'Đang gửi…' : 'Gửi đề xuất'}
            </button>
          )}
          <button type="button" disabled={sending || uploading} onClick={onClose} className="rounded-lg border ui-divider px-4 py-2 text-sm font-semibold ui-text">
            Huỷ
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandProposalComposer;
